// Column header tabs as a button grid (JDEV-3370, JDEV-3379): how many columns the grid gets.
//
// The user interface setting "Column tabs" (client.columntabs, UserinterfaceDetails.vue) has three values:
//   row        one row with a "Lisää (n)" menu (JDEV-3207, useTabOverflow; also NULL and anything unknown)
//   grid       every tab is a pill in a grid; as many columns as fit cells of at least GRID_MIN_CELL px
//   gridfixed  every tab is a pill in a grid; at most a fixed column count by the bar's width (FIXED_STEPS)
// Below 768px (viewport) both grid values fall back to the row (ColumnHeader.vue).
//
// The column count is computed here, not with CSS auto-fill, so the last row never holds a single cell when the
// rows above can take it (Saku, 2026-11-01). The cells are the tabs plus the add (+) button, which is the last cell.
//
// gridColumns(n, width, mode, content):
//  1. One row by content (JDEV-3379): if every cell fits at its natural width (`content`: the cells' widths with
//     untruncated labels, gaps included), one row of n columns in both modes. ColumnTabs sizes that row's
//     columns by their content (CSS minmax(0, auto)), so no label is cut while there is room.
//  2. base: "grid" = the most columns of at least GRID_MIN_CELL px that fit (gaps included), at least 1;
//     "gridfixed" = FIXED_STEPS by the width (6 above 1100px, 5 above 900, 4 above 640, 3 above 440, else 2).
//  3. n <= base: one row of n columns (never more columns than cells), content-sized as in 1; the long labels
//     share what is left after the short ones.
//  4. Squeeze: if base columns would leave one cell alone on the last row, take one column more when its cells
//     stay at least SQUEEZE_MIN_CELL px. The orphan moves up into the rows above (9 cells with room for 8: one
//     row of 9; 13 fixed at 6: 7 + 6 instead of 6 + 6 + 1).
//  5. Balance ("grid" only): rows = ceil(n / columns), columns = ceil(n / rows), so the rows are as even as
//     possible: 13 cells with room for 8 make 7 + 6 instead of 8 + 5. "gridfixed" keeps its count.
//  6. If the last row still has one cell (rows > 1), try one column more (if its cells stay at least
//     SQUEEZE_MIN_CELL px; "grid" balances again), then one column less if that needs no extra row. The first
//     that leaves 2+ cells on the last row wins. If neither does (2 columns and an odd count in a narrow column,
//     or 13 cells with room for 3), the orphan stays: an extra row would cost more height than it is worth.
// Pure and exported for tests; useTabGrid() feeds it the bar's width and the cells' natural widths.

import { nextTick, onBeforeUnmount, onMounted, ref, watch, type Ref, type WatchSource } from 'vue'

export type ColumnTabsMode = 'row' | 'grid' | 'gridfixed'

export const GRID_GAP = 6 // px, the gap between pills in a row (the CSS column-gap is the same)
export const GRID_MIN_CELL = 150 // px, the narrowest pill in "grid" mode (about 12 letters at 16px)
export const SQUEEZE_MIN_CELL = 120 // px, the narrowest pill when an orphan is squeezed into the rows above
export const ADD_CELL = 32 // px, the add (+) button's width in a content-sized row (square, as high as a pill)
const FIXED_STEPS: Array<[number, number]> = [[1100, 6], [900, 5], [640, 4], [440, 3]]

export function normalizeColumnTabsMode (value: unknown): ColumnTabsMode {
  return value === 'grid' || value === 'gridfixed' ? value : 'row'
}

const cellWidth = (width: number, cols: number) => (width - GRID_GAP * (cols - 1)) / cols
const rowsFor = (n: number, cols: number) => Math.ceil(n / cols)
const lastRow = (n: number, cols: number) => n - (rowsFor(n, cols) - 1) * cols

export function baseColumns (width: number, mode: ColumnTabsMode) {
  if (mode === 'gridfixed') {
    for (const [min, cols] of FIXED_STEPS) if (width > min) return cols
    return 2
  }
  return Math.max(1, Math.floor((width + GRID_GAP) / (GRID_MIN_CELL + GRID_GAP)))
}

/** Columns for n cells in a bar `width` px wide; `content` is the cells' natural width in one row (0: unknown). */
export function gridColumns (n: number, width: number, mode: ColumnTabsMode, content = 0) {
  if (n < 1) return 1
  if (content > 0 && content <= width) return n
  const base = baseColumns(width, mode)
  if (n <= base) return n
  const fits = (cols: number) => cellWidth(width, cols) >= SQUEEZE_MIN_CELL
  const balance = (cols: number) => (mode === 'grid' ? Math.ceil(n / rowsFor(n, cols)) : cols)
  const ok = (cols: number) => rowsFor(n, cols) === 1 || lastRow(n, cols) !== 1
  let cols = lastRow(n, base) === 1 && fits(base + 1) ? base + 1 : base
  cols = balance(cols)
  if (ok(cols)) return cols
  if (fits(cols + 1) && ok(balance(cols + 1))) return balance(cols + 1)
  const less = cols - 1
  if (less >= 2 && rowsFor(n, less) === rowsFor(n, cols) && ok(less)) return less
  return cols
}

/**
 * The natural (untruncated) width of the cells in `list` in one row, gaps included; 0 if a cell is not laid
 * out yet. A tab pill's width is its chrome (padding, actions, extras) plus its label's full text width
 * (scrollWidth), which does not depend on the column it is in, so measuring never feeds back into the result.
 */
export function naturalRowWidth (list: HTMLElement | null) {
  if (!list) return 0
  const cells = [...list.children].filter((el): el is HTMLElement => el instanceof HTMLElement && !el.hidden)
  if (!cells.length) return 0
  let total = GRID_GAP * (cells.length - 1)
  for (const cell of cells) {
    const tab = cell.querySelector<HTMLElement>('.j-tab')
    const label = tab?.querySelector<HTMLElement>('.j-tab-label')
    if (!tab || !label) { // the add (+) button
      total += ADD_CELL
      continue
    }
    if (!cell.offsetWidth) return 0
    const cs = getComputedStyle(tab)
    let inner = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
    const parts = [...tab.children].filter((el): el is HTMLElement => el instanceof HTMLElement && el.getClientRects().length > 0)
    for (const part of parts) inner += part === label ? label.scrollWidth : part.getBoundingClientRect().width
    inner += (parseFloat(cs.columnGap) || 0) * Math.max(0, parts.length - 1)
    total += cell.getBoundingClientRect().width - tab.getBoundingClientRect().width + inner + 1 // 1px: subpixel text
  }
  return Math.ceil(total)
}

/**
 * Tracks the bar's content width and the cells' natural width; returns the column count (0 while the grid is
 * off) and whether all cells are on one row (ColumnTabs then sizes the columns by their content).
 */
export function useTabGrid (options: {
  bar: Ref<HTMLElement | null>,
  list: Ref<HTMLElement | null>,
  mode: Ref<ColumnTabsMode | null | undefined>,
  count: Ref<number>,
  watch?: WatchSource[] // anything that changes a cell's natural width (labels, counters)
}) {
  const columns = ref(0)
  const oneRow = ref(false)
  const width = ref(0)
  let retry = 0
  let tries = 0
  const update = () => {
    const mode = options.mode.value
    if (!mode || mode === 'row' || width.value <= 0) {
      columns.value = 0
      oneRow.value = false
      return
    }
    const n = options.count.value
    const content = naturalRowWidth(options.list.value)
    columns.value = gridColumns(n, width.value, mode, content)
    oneRow.value = columns.value >= n
    // Cells not laid out yet (just switched from the row): measure again on the next frames, a few times.
    if (content) tries = 0
    else if (!retry && tries < 10 && typeof requestAnimationFrame !== 'undefined') {
      tries++
      retry = requestAnimationFrame(() => { retry = 0; update() })
    }
  }
  const schedule = () => { nextTick(update) }
  const read = (el: HTMLElement) => {
    const cs = getComputedStyle(el)
    width.value = Math.floor(el.clientWidth - (parseFloat(cs.paddingInlineStart) || 0) - (parseFloat(cs.paddingInlineEnd) || 0))
  }
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => { if (options.bar.value) read(options.bar.value) })
  watch(options.bar, (el, old) => {
    if (old) observer?.unobserve(old)
    if (el) { observer?.observe(el); read(el) }
  }, { immediate: true, flush: 'post' })
  watch([width, options.mode, options.count, ...(options.watch || [])], schedule, { immediate: true, flush: 'post' })
  onMounted(() => { document.fonts?.ready.then(schedule) }) // label widths change when the web font arrives
  onBeforeUnmount(() => {
    observer?.disconnect()
    if (retry) cancelAnimationFrame(retry)
  })
  return { columns, oneRow }
}
